import type React from 'react';

import { useGetFace } from '@vlabs/api/luna-api/client';
import type { FaceId } from '@vlabs/api/luna-api/model/FaceId';
import type { Similarity } from '@vlabs/api/luna-api/model/Similarity';
import { useGetCard } from '@vlabs/api/point-api/client';
import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { calcTagType } from '@vlabs/shared/utils';
import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useTopMatchCardInfoTranslation } from './i18n';
import st from './TopMatchCardInfo.module.sass';
import { buildTestid } from '../../../shared/lib/utils';
import { EventFaceTopMatchSample } from '../detection-sample';

interface TopMatchCardInfoProps extends WithDataTestid {
  faceId: FaceId;
  similarity: Similarity;
}

const displayName = 'TopMatchCardInfo';
const testid = buildTestid(displayName);

function TopMatchCardInfo({
  faceId,
  similarity,
  'data-testid': dataTestId = undefined,
}: TopMatchCardInfoProps): React.JSX.Element | null {
  const { t } = useTopMatchCardInfoTranslation();
  const { data: face } = useGetFace(faceId, { targets: ['external_id'] });
  const { data: card } = useGetCard(
    face?.external_id ?? '',
    { targets: ['user_data'] },
    { query: { enabled: !!face?.external_id } },
  );

  if (face && !face.external_id) return null;

  const kind = calcTagType(similarity);
  const fio = card?.user_data?.fio as string;

  return (
    <div
      className={cn(st.TopMatchCardInfo, {
        [st.TopMatchCardInfo_positive]: kind === 'positive',
        [st.TopMatchCardInfo_attention]: kind === 'attention',
        [st.TopMatchCardInfo_negative]: kind === 'negative',
      })}
      {...testid(dataTestId)}
    >
      <EventFaceTopMatchSample
        className={st.Sample}
        faceId={faceId}
      />
      <div className={st.Info}>
        {!!similarity && (
          <SimilarityTag
            icon={<DetectionIcon />}
            size="large"
            title={t('tag')}
            value={similarity}
          />
        )}

        {!!fio && (
          <div className={st.Fio}>
            <span className={st.Title}>{t('fio')}</span>
            <span className={st.Value}>{fio}</span>
          </div>
        )}
      </div>
    </div>
  );
}

TopMatchCardInfo.displayName = displayName;

export { TopMatchCardInfo };
